import { openDB } from 'idb';
import api from './api';

const DB_NAME = 'vodovod-offline';
const STORE_NAME = 'pending-readings';

async function getDB() {
  return openDB(DB_NAME, 2, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    },
  });
}

export async function saveOfflineReading(readingData, photoFile) {
  const db = await getDB();
  const entry = {
    ...readingData,
    savedAt: new Date().toISOString(),
  };

  // Store photo as ArrayBuffer (survives page refresh)
  if (photoFile) {
    entry.photoBuffer = await photoFile.arrayBuffer();
    entry.photoName = photoFile.name || 'photo.jpg';
    entry.photoType = photoFile.type || 'image/jpeg';
  }

  await db.add(STORE_NAME, entry);
}

export async function getPendingCount() {
  const db = await getDB();
  return db.count(STORE_NAME);
}

export async function getPendingReadings() {
  const db = await getDB();
  return db.getAll(STORE_NAME);
}

export async function syncPendingReadings(onProgress) {
  const db = await getDB();
  const pending = await db.getAll(STORE_NAME);
  let synced = 0;
  let conflicts = 0;
  let errors = 0;

  for (const reading of pending) {
    try {
      const formData = new FormData();
      formData.append('meter_id', reading.meter_id || '');
      formData.append('meter_number_manual', reading.meter_number_manual || '');
      formData.append('value', reading.value);
      formData.append('ocr_used', reading.ocr_used || false);
      formData.append('ocr_raw_text', reading.ocr_raw_text || '');
      formData.append('note', reading.note || '');
      formData.append('gps_lat', reading.gps_lat || '');
      formData.append('gps_lng', reading.gps_lng || '');
      formData.append('gps_accuracy', reading.gps_accuracy || '');
      if (reading.read_at) formData.append('read_at', reading.read_at);

      if (reading.photoBuffer) {
        const blob = new Blob([reading.photoBuffer], { type: reading.photoType || 'image/jpeg' });
        formData.append('photo', blob, reading.photoName || 'photo.jpg');
      }

      await api.post('/readings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await db.delete(STORE_NAME, reading.id);
      synced++;
      if (onProgress) onProgress(synced, pending.length, conflicts);
    } catch (err) {
      if (err.response?.status === 409) {
        // Conflict — meter already read by another user this month.
        // Mark as conflicted but continue syncing the rest.
        conflicts++;
        console.warn('Sync conflict for reading:', reading.meter_id || reading.meter_number_manual, err.response?.data?.error);
        try {
          const tx = db.transaction(STORE_NAME, 'readwrite');
          const store = tx.objectStore(STORE_NAME);
          await store.put({ ...reading, conflicted: true, conflictMessage: err.response?.data?.error || 'Već očitano' });
          await tx.done;
        } catch (e) { console.error('Failed to mark conflict:', e); }
        continue; // continue syncing remaining readings
      } else {
        console.error('Failed to sync reading:', err);
        errors++;
        continue; // don't break — try the rest
      }
    }
  }

  return { synced, total: pending.length, conflicts, errors };
}

export async function getConflictedReadings() {
  const db = await getDB();
  const all = await db.getAll(STORE_NAME);
  return all.filter(r => r.conflicted);
}

export async function clearConflictedReading(id) {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
}

// Auto-sync when online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    syncPendingReadings((s, t) => {
      console.log(`Syncing: ${s}/${t}`);
    });
  });
}
